import { css, keyframes } from 'goober';
import { isEmpty, isFunction, isString, isUndefined } from 'mc-ui-utils';
import React, { Component, createRef } from 'react';
import constants from '../../constants';
import State from '../../state';
import colors from '../../styles/colors';
import { getViewPortDimensions } from '../../utils/common';
import { startPendingProgressBarAnimation } from '../../utils/progressbarhelper';
import { initializeYoutubePlayer } from '../../utils/videohelper';
import { connectComponent } from '../Provider';
const Container = css `
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  will-change: opacity;
  opacity: 1;
  touch-action: none;
  iframe {
    pointer-events: none;
    touch-action: none;
  }
`;
const LoaderAnimation = keyframes `
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;
const Loader = css `
  border: 3px solid ${colors.concrete};
  animation: ${LoaderAnimation} 1s linear infinite;
  border-top: 3px solid ${colors.emperor};
  border-radius: 50%;
  width: 50px;
  height: 50px;
  position: absolute;
  left: calc(50% - 25px);
  top: calc(50% - 25px);
`;
class YoutubeVideo extends Component {
    constructor(props) {
        var _a, _b, _c;
        super(props);
        this.container = createRef();
        this.youtubePlayer = createRef();
        this._isMounted = false; //changed from isMounted to _isMounted to avoid react #<Component> conflict
        this.startTime = 0;
        this.videoLoadFailure = 0;
        this.playCallDuration = null;
        this.playStartDuration = null;
        this.initializePlayer = () => {
            var _a;
            if (isFunction((_a = window === null || window === void 0 ? void 0 : window.YT) === null || _a === void 0 ? void 0 : _a.Player)) {
                const { isMuted } = this.props;
                this.loaded = Date.now();
                this.youtubePlayer.current = initializeYoutubePlayer(this.container.current, this.videoId, isMuted, this.onPlayerReady, this.onPlayerStateChange, this.onError);
            }
            else if (window.youtubeScriptLoading) {
                // some other video has triggered the script load,
                const checkScriptHasLoaded = () => {
                    if (!window.youtubeScriptLoading) {
                        // initializePlayer();
                        clearTimeout(this.timer);
                    }
                    else {
                        this.timer = window.setTimeout(() => {
                            checkScriptHasLoaded();
                        }, 100);
                    }
                };
                this.timer = window.setTimeout(() => {
                    checkScriptHasLoaded();
                }, 100);
            }
        };
        this.toggleAudio = () => {
            var _a;
            const { isMuted } = this.props;
            if (isFunction((_a = this.youtubePlayer.current) === null || _a === void 0 ? void 0 : _a.pauseVideo)) {
                if (isMuted) {
                    this.youtubePlayer.current.mute();
                }
                else {
                    this.youtubePlayer.current.unMute();
                }
            }
        };
        this.toggleHolding = () => {
            var _a;
            const { isHolding, isCurrentGlance } = this.props;
            if (isCurrentGlance && isFunction((_a = this.youtubePlayer.current) === null || _a === void 0 ? void 0 : _a.pauseVideo)) {
                if (isHolding) {
                    this.youtubePlayer.current.pauseVideo();
                }
                else {
                    this.youtubePlayer.current.playVideo();
                }
            }
        };
        this.fireVideoDetails = () => {
            var _a, _b, _c;
            const { bubbleEvent, glanceId } = this.props;
            let totalVideoWatchDuration = 0;
            if (isFunction((_b = (_a = this.youtubePlayer) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.getDuration)) {
                totalVideoWatchDuration = (_c = this.youtubePlayer) === null || _c === void 0 ? void 0 : _c.current.getDuration();
            }
            const videoDetails = {
                videoDuration: State.getVideoDuration(glanceId),
                videoFailureCount: this.videoLoadFailure,
                videoLoadDuration: this.loadingTime,
                playCallDuration: this.playCallDuration,
                playStartDuration: this.playStartDuration,
                videoTime: this.startTime,
                totalVideoWatchDuration,
            };
            bubbleEvent.setGlanceVideoDetails(glanceId, videoDetails);
        };
        this.playVideo = (glanceId, isMuted) => {
            const videoTimeline = State.getVideoTimeline(glanceId);
            if (videoTimeline) {
                this.youtubePlayer.current.seekTo(videoTimeline, true);
            }
            if (isMuted) {
                this.youtubePlayer.current.mute();
            }
            else {
                this.youtubePlayer.current.unMute();
            }
            this.youtubePlayer.current.playVideo();
            const playCallDuration = Date.now() - this.toggling;
            this.playCallDuration = playCallDuration;
        };
        this.onPlayerReady = () => {
            requestAnimationFrame(() => {
                var _a, _b;
                this.loadingTime = Date.now() - this.loaded;
                const { glanceId, isCurrentGlance, isMuted, startPendingAutoPlay, } = this.props;
                if (this._isMounted) {
                    if (isCurrentGlance) {
                        (_a = this.youtubePlayer.current) === null || _a === void 0 ? void 0 : _a.playVideo();
                        const playCallDuration = Date.now() - this.toggling;
                        this.playCallDuration = playCallDuration;
                        if (isMuted) {
                            this.youtubePlayer.current.mute();
                        }
                        else {
                            this.youtubePlayer.current.unMute();
                        }
                    }
                    const duration = (_b = this.youtubePlayer.current) === null || _b === void 0 ? void 0 : _b.getDuration();
                    if (!isEmpty(duration)) {
                        State.setStoryDuration(glanceId, duration * 1000);
                        // change current animation duration if playing
                        if (isCurrentGlance) {
                            startPendingProgressBarAnimation(glanceId, duration * 1000);
                            startPendingAutoPlay();
                        }
                    }
                    this.setState({
                        isReady: true,
                    });
                }
            });
        };
        this.onPlayerStateChange = (event) => {
            if (event.data === YT.PlayerState.PLAYING) {
                this.playStartDuration = Date.now() - this.toggling;
            }
        };
        this.onError = () => {
            this.setState({
                isEmbedNotAllowed: true,
            });
            this.videoLoadFailure = this.videoLoadFailure + 1;
        };
        const { video } = props;
        this.videoId = (_a = video === null || video === void 0 ? void 0 : video.youtube) === null || _a === void 0 ? void 0 : _a.id;
        this.state = {
            isReady: false,
            isEmbedNotAllowed: false,
        };
        if (!isEmpty((_c = (_b = video.youtube) === null || _b === void 0 ? void 0 : _b.ration) === null || _c === void 0 ? void 0 : _c.height)) {
            this.height =
                parseInt(getViewPortDimensions().vw, 10) *
                    (video.youtube.ration.height / video.youtube.ration.width);
        }
        this.heightClass = css `
      height: ${!isEmpty(this.height) ? this.height : 345}px;
    `;
    }
    componentDidMount() {
        const { isCurrentGlance, glanceId, isMuted } = this.props;
        /* const thumb = new Image();
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const context = this;
        const youtubeThumbnail = constants.YOUTUBE_THUMBNAIL(this.videoId);
        thumb.onload = function () {
          if (
            (this as HTMLImageElement).width > 200 &&
            (this as HTMLImageElement).height > 100
          ) {
            context.setState({
              youtubeThumbnail,
            });
          }
        };
        thumb.src = youtubeThumbnail; */
        this._isMounted = true;
        const { shouldLoad } = this.props;
        const { isReady } = this.state;
        if (shouldLoad && !isReady) {
            this.initializePlayer();
        }
    }
    componentWillUnmount() {
        this._isMounted = false;
        if (!isUndefined(this.timer)) {
            clearTimeout(this.timer);
        }
    }
    shouldComponentUpdate(nextProps) {
        const { isCurrentGlance } = this.props;
        return isCurrentGlance || nextProps.isCurrentGlance;
    }
    componentDidUpdate(prevProps) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const { isMuted, isHolding, isCurrentGlance, glanceId, shouldLoad, isHomeVisible, informStateUpdatedOnInvisible, } = this.props;
        if (isMuted !== prevProps.isMuted) {
            this.toggleAudio();
        }
        if (isHolding !== prevProps.isHolding) {
            this.toggleHolding();
        }
        if (!prevProps.shouldLoad && shouldLoad) {
            this.initializePlayer();
        }
        if (!isHomeVisible && prevProps.isHomeVisible && isCurrentGlance) {
            if (isFunction((_b = (_a = this.youtubePlayer) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.pauseVideo)) {
                this.youtubePlayer.current.pauseVideo();
            }
            this.fireVideoDetails();
            informStateUpdatedOnInvisible();
        }
        else if (isHomeVisible &&
            !prevProps.isHomeVisible &&
            isCurrentGlance &&
            isFunction((_d = (_c = this.youtubePlayer) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.pauseVideo)) {
            this.playVideo(glanceId, isMuted);
        }
        if (isCurrentGlance !== prevProps.isCurrentGlance) {
            if (!isCurrentGlance) {
                //capture video details and fire glanceimpression
                this.fireVideoDetails();
                if (isFunction((_f = (_e = this.youtubePlayer) === null || _e === void 0 ? void 0 : _e.current) === null || _f === void 0 ? void 0 : _f.pauseVideo)) {
                    State.setVideoTimeline(glanceId, this.youtubePlayer.current.getCurrentTime());
                    this.youtubePlayer.current.pauseVideo();
                }
            }
            if (isCurrentGlance) {
                this.toggling = Date.now();
                isFunction((_h = (_g = this.youtubePlayer) === null || _g === void 0 ? void 0 : _g.current) === null || _h === void 0 ? void 0 : _h.pauseVideo)
                    ? this.playVideo(glanceId, isMuted)
                    : null;
            }
        }
    }
    render() {
        const { type, glanceId, shouldLoad } = this.props;
        const { isReady, isEmbedNotAllowed } = this.state;
        if (!(type === 2 /* VIDEO */ && isString(this.videoId))) {
            return null;
        }
        if (isEmbedNotAllowed) {
            return null;
        }
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: Container },
                React.createElement("div", { className: this.heightClass, ref: this.container, id: `${glanceId}-player` })),
            shouldLoad && !isReady && !constants.HAS_YOUTUBE_AUTOPLAY && (React.createElement("div", { className: Loader }))));
    }
}
const mapStateToProps = (state) => ({
    isMuted: state.isMuted,
});
export default connectComponent(mapStateToProps)(YoutubeVideo);
//# sourceMappingURL=index.js.map